import React from "react";
import { motion } from "framer-motion";
import { RefreshCcw } from "lucide-react";
import { useApp } from "../context/AppContext";
import { formatUSD } from "../lib/format";

const slide = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -30 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const BreakdownTable = ({ breakdown, inflationRate }) => {
  const ratePct = (inflationRate * 100).toFixed(1).replace(/\.0$/, "");
  // Build rows: prevCuota | inflation amount | total
  const rows = breakdown.map((row, i) => {
    const prev = i === 0 ? row.annualCost : breakdown[i - 1].annualCost;
    const inflation = row.annualCost - prev;
    return {
      year: row.year,
      prev,
      inflation,
      total: row.annualCost,
    };
  });
  return (
    <div className="mt-12 border-t border-white/10 pt-10" data-testid="breakdown-table">
      <p className="text-[10px] uppercase tracking-luxury text-[#D4AF37] sm:text-xs">
        Corrida año por año · Year by year
      </p>
      <div className="mt-6 overflow-hidden border border-white/10">
        <table className="w-full text-left text-xs sm:text-sm">
          <thead className="bg-white/[0.04] text-[10px] uppercase tracking-luxury text-white/50 sm:text-xs">
            <tr>
              <th className="px-3 py-3 sm:px-4">Año</th>
              <th className="px-3 py-3 text-right sm:px-4">Cuota USD</th>
              <th className="px-3 py-3 text-right sm:px-4">Inflación {ratePct}%</th>
              <th className="px-3 py-3 text-right sm:px-4">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.year} className="border-t border-white/5">
                <td className="px-3 py-2.5 font-display text-white sm:px-4">{r.year}</td>
                <td className="px-3 py-2.5 text-right text-white/70 sm:px-4">
                  {formatUSD(r.prev)}
                </td>
                <td className="px-3 py-2.5 text-right text-[#D4AF37] sm:px-4">
                  {r.inflation === 0 ? "—" : formatUSD(r.inflation)}
                </td>
                <td className="px-3 py-2.5 text-right font-display text-white sm:px-4">
                  {formatUSD(r.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ResultCard = ({ result, onReset }) => {
  const { t, settings } = useApp();
  if (!result) return null;

  const isNoMembership = result.scenario === "no_membership";

  return (
    <motion.div key="result" {...slide} className="text-center" data-testid="result-section">
      <p className="text-xs tracking-luxury text-[#D4AF37]">
        {t("result_title_a")} {settings.end_year}
      </p>
      <h2 className="mt-3 font-display text-xl text-white/80 sm:text-2xl">
        {isNoMembership ? t("result_title_b") : t("result_title_b_with")}
      </h2>

      {/* Total — huge */}
      <div className="mt-10">
        <p className="text-[10px] uppercase tracking-luxury text-white/40 sm:text-xs">
          {t("result_total_label")}
        </p>
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mt-3 font-display text-6xl leading-none text-white sm:text-8xl"
          data-testid="result-total"
        >
          {formatUSD(result.total)}
        </motion.div>
      </div>

      <p className="mt-8 text-[10px] uppercase tracking-wider text-white/30 sm:text-xs">
        {t("result_disclaimer")}
      </p>

      {/* Year by year breakdown — only for with_membership */}
      {!isNoMembership && (
        <BreakdownTable
          breakdown={result.breakdown}
          inflationRate={result.inflationRate}
        />
      )}

      <div className="mt-12 flex justify-center">
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-2 border border-white/15 px-8 py-4 text-xs uppercase tracking-luxury text-white/80 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
          data-testid="result-reset-btn"
        >
          <RefreshCcw size={14} /> {t("btn_recalc")}
        </button>
      </div>
    </motion.div>
  );
};

export default ResultCard;
